<script setup lang="ts">
import {
  DsAccordion,
  DsAlert,
  DsButton, DsFieldError,
  DsForm,
  DsFormGroup,
  DsInput, DsPhoneNumberInput,
  NotificationVariant,
  useNotification
} from '@demvsystems/design-components';
import { requestMaklerVollmacht } from "@/api/maklerVollmacht/maklerVollmachtApi";
import { useForm } from "@/api/lib/integration";
import { onMounted, ref } from "vue";
import { useBaseDataStore } from "@/user/store/baseDataStore";
import { useUserDataStore } from "@/user/store/userDataStore";
import { storeToRefs } from "pinia";

const emit = defineEmits(['cancel', 'success']);
const { userData } = storeToRefs(useUserDataStore());
const { duzen } = useBaseDataStore();
const invalidMobil = ref(false);

const { form, errors, submit, updateField, updateForm, loading } = useForm(
  requestMaklerVollmacht,
   {
    street: userData.value?.mainAddress?.street ?? '',
    houseNumber: userData.value?.mainAddress?.houseNumber ?? '',
    zipCode: userData.value?.mainAddress?.zipCode ? `${userData.value?.mainAddress?.zipCode}` : '',
    city: userData.value?.mainAddress?.city ?? '',
    mobile: userData.value?.mobile ?? '',
  },
  ({ status }) => {
    userData.value = {
      ...userData.value ?? {
        pwEmail: null,
        phone: null,
      },
      mainAddress: {
        street: form.value.street,
        city: form.value.city,
        houseNumber: form.value.houseNumber,
        zipCode: Number(form.value.zipCode),
      },
      mobile: form.value.mobile,
    };
    useBaseDataStore().maklervollmachtStatus = status;
    useNotification().show(
      `Der Maklervertrag bzw. -vollmacht wird vorbereitet. Dies kann einige Minuten dauern.`,
      NotificationVariant.Success,
    );
    emit('success');
  },
  () => {
    useNotification().show(
      `Möglicherweise sind noch nicht alle Voraussetzungen erfüllt. ${duzen('Versuche', 'Versuchen Sie')}
      es zu einem späteren Zeitpunkt erneut.`,
      NotificationVariant.Error,
    );
  },
);

const saveSubmit = async () => {
  if (invalidMobil.value) return;

  await submit();
}

onMounted(async() => {
  if (!useUserDataStore().userData?.mainAddress) {
    loading.value = true;
    await useUserDataStore().fetchUserContacts();
    updateForm({
      street: userData.value?.mainAddress?.street ?? form.value.street ?? '',
      houseNumber: userData.value?.mainAddress?.houseNumber ?? form.value.houseNumber ?? '',
      zipCode: userData.value?.mainAddress?.zipCode ? `${userData.value?.mainAddress?.zipCode}` : form.value.zipCode ?? '',
      city: userData.value?.mainAddress?.city ?? form.value.city ?? '',
      mobile: userData.value?.mobile ?? form.value.mobile ?? '',
    })
    loading.value = false;
  }
});
</script>

<template>
  <div class="flex flex-col gap-3 justify-between h-full">
    <div class="flex flex-col gap-2 border border-gray-200 rounded-lg py-2 px-3 bg-white mb-auto">
      <p class="text-sm text-gray-800 mb-2">
        Damit {{ duzen('dein Berater dich', 'Ihr Berater Sie ') }} gegenüber den jeweiligen Versicherern vertreten
        kann, wird ein Maklervertrag bzw. -vollmacht benötigt. Diese {{ duzen('kannst du', 'können Sie') }} mit
        wenigen Klicks anfordern und unterschreiben. {{ duzen('Prüfe', 'Prüfen Sie') }} vorher bitte, ob
        {{ duzen('deine', 'Ihre') }} Kontaktdaten vollständig und aktuell sind. Wenn das Maklerdokument
        unterschrieben ist, {{ duzen('kannst du', 'können Sie') }} es bei den Dokumenten aufrufen.
      </p>
      <DsAlert type="primary" class="pt-3">
        <DsAccordion>
          <template #trigger>
            <h3 class="text-sm leading-5 font-semibold text-blue-800 -m-1 p-0">Wieso werden diese Daten benötigt?</h3>
          </template>
          <template #default>
            <ul class="pt-2 space-y-2">
              <li>
                Die Angabe Ihrer Adresse wird für den Maklervertrag bzw. -vollmacht benötigt.
              </li>
              <li>
                Die Handynummer ist für den Identifikationsprozess der Unterschrift notwendig. Bei Aufruf des Dokuments
                {{ duzen('erhältst du', 'erhalten Sie') }} einen 6-stelligen Code per SMS. (Ihnen entstehen keine Kosten)
              </li>
            </ul>
          </template>
        </DsAccordion>
      </DsAlert>
    </div>

    <DsForm :validation-errors="errors">
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-4 md:flex-row justify-between">
          <DsFormGroup label="Straße" label-for="Straße" class="w-full" validation-name="street">
            <DsInput id="Straße" :model-value="form.street" size="lg" @update:model-value="updateField('street', $event)" />
          </DsFormGroup>
          <DsFormGroup label="Hausnummer" label-for="Hausnummer" class="w-full md:w-52" validation-name="houseNumber">
            <DsInput id="Hausnummer" :model-value="form.houseNumber" size="lg" @update:model-value="updateField('houseNumber', $event)" />
          </DsFormGroup>
        </div>
        <div class="flex flex-col gap-4 md:flex-row justify-between">
          <DsFormGroup label="Postleitzahl" label-for="Postleitzahl" class="w-full md:w-52" validation-name="zipCode">
            <DsInput id="Postleitzahl" :model-value="form.zipCode" mask="#####" size="lg" @update:model-value="updateField('zipCode', $event)" />
          </DsFormGroup>
          <DsFormGroup label="Stadt" label-for="Stadt" class="w-full" validation-name="city">
            <DsInput id="Stadt" :model-value="form.city" size="lg" @update:model-value="updateField('city', $event)" />
          </DsFormGroup>
        </div>
        <DsFormGroup validation-name="mobile" label="Mobilfunknummer" label-for="Mobilfunknummer">
          <DsPhoneNumberInput
            id="Mobilfunknummer"
            :model-value="form.mobile"
            immediate
            size="lg"
            mobile
            @update:model-value="updateField('mobile', $event)"
            @valid="invalidMobil = false"
            @invalid="invalidMobil = true"
          />
          <DsFieldError v-if="invalidMobil">Ungültiges Mobilfunknummer Format</DsFieldError>
        </DsFormGroup>
      </div>
    </DsForm>

    <div class="sticky bottom-0 mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
      <DsButton
        :disabled="invalidMobil"
        :handler="saveSubmit"
        size="lg"
        class="w-full sm:ml-3 sm:w-auto"
        variant="primary"
      >
        Jetzt anfordern
      </DsButton>
    </div>
  </div>
</template>
